import { useEffect, useState } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import MobileLayout from './MobileLayout';
import Footer from './Footer';
import { useLocation } from 'react-router-dom';
import useViewport from '../../hooks/useViewport';
import WelcomeTourModal from '../tour/WelcomeTourModal';
import { useWelcomeTour } from '../tour/useWelcomeTour';
import { useAuthContext } from '../../features/auth/context/AuthContext';

// Debug flag to control viewport logging
const DEBUG_VIEWPORT = false;

/**
 * Main Layout Component
 * 🎨 Provides the basic structure for authenticated pages
 * Automatically switches between desktop and mobile layouts based on screen size
 */
const Layout = ({ children }) => {
  const location = useLocation();
  const { isMobile, width } = useViewport();
  const { isAuthenticated } = useAuthContext();
  const isTransactionsPage = location.pathname.includes('/transactions');
  
  // Welcome tour management
  const { isTourOpen, openTour, closeTour, completeTour } = useWelcomeTour(isAuthenticated);
  
  // For transaction pages, completely remove background and blur effects
  // For other pages, use very transparent background to show body gradient
  const mainClass = isTransactionsPage
    ? "flex-1 p-6 bg-transparent" 
    : "flex-1 p-6 bg-white/10 backdrop-blur-[2px]";
  
  // Debug mobile detection (disabled for production)
  // useEffect(() => {
  //   console.log('🔍 Layout Debug:', { 
  //     width, 
  //     isMobile, 
  //     userAgent: navigator.userAgent,
  //     willUseMobileLayout: isMobile || width < 768
  //   });
  // }, [width, isMobile]);
  
  // Force body to prevent horizontal overflow on mobile
  useEffect(() => {
    if (isMobile) {
      document.body.classList.add('overflow-prevent');
      // Prevent body scrolling when mobile menu is active
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('overflow-prevent');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.classList.remove('overflow-prevent');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isMobile]);
  
  // Use mobile layout for small screens or mobile devices
  // Force mobile layout for devices less than 1024px wide OR mobile user agents
  const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent);
  const isPortraitMobile = width < 1024 && window.innerHeight > width;
  const shouldUseMobileLayout = isMobile || width < 1024 || isMobileUserAgent || isPortraitMobile;
  
  if (shouldUseMobileLayout) {
    return <MobileLayout>{children}</MobileLayout>;
  }
    
  // Desktop layout for larger screens
  return (
    <>
      <div className="h-screen flex flex-col overflow-hidden">
        <Navbar onOpenTour={openTour} />
        <div className="flex flex-1 min-h-0">
          <Sidebar />
          <main className={`${mainClass} overflow-y-auto overflow-x-hidden`}>
            {children}
          </main>
        </div>
        <Footer />
      </div>
      
      {/* Welcome Tour Modal */}
      <WelcomeTourModal
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={completeTour}
      />
    </>
  );
};

export default Layout; 